'use client';
import React from 'react';
import clsx from 'clsx';
import { HiOutlineExternalLink } from 'react-icons/hi';

export interface GovTradesTabItem {
  key: string;
  name: string;
  href?: string;
  external?: boolean;
}

interface GovTradesTabsBarProps {
  tabs: GovTradesTabItem[];
  activeKey: string;
  onSelect: (key: string) => void;
}

const TabLabel = ({ children, active, accent }: { children: React.ReactNode; active?: boolean; accent?: boolean }) => (
  <div
    className={clsx(
      'whitespace-nowrap text-[15px] font-bold leading-normal',
      accent || active ? 'text-[#3f83f8]' : 'text-bzgray-600',
    )}
  >
    {children}
  </div>
);

const TabLink = ({ tab }: { tab: GovTradesTabItem }) => (
  <a
    aria-selected={false}
    className="ml-auto flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap border-0 bg-transparent px-5 py-3 transition-[box-shadow] duration-150"
    href={tab.href}
    rel={tab.external ? 'noreferrer' : undefined}
    role="tab"
    target={tab.external ? '_blank' : undefined}
  >
    <div className="flex w-full items-center justify-center gap-1">
      <TabLabel accent>{tab.name}</TabLabel>
      {tab.external ? <HiOutlineExternalLink aria-hidden className="shrink-0 text-[#3f83f8]" size={14} /> : null}
    </div>
  </a>
);

const TabButton = ({ tab, active, onSelect }: { tab: GovTradesTabItem; active: boolean; onSelect: (key: string) => void }) => (
  <button
    aria-selected={active}
    className={clsx(
      'flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap border-0 bg-transparent px-5 py-3 transition-[box-shadow] duration-150 [&:hover:not([aria-selected=true])_div]:text-bzgray-900',
      active && 'shadow-[inset_0_-3px_0_#3f83f8]',
    )}
    onClick={() => onSelect(tab.key)}
    role="tab"
    type="button"
  >
    <div className="flex w-full items-center justify-center">
      <TabLabel active={active}>{tab.name}</TabLabel>
    </div>
  </button>
);

export const GovTradesTabsBar = ({ tabs, activeKey, onSelect }: GovTradesTabsBarProps) => {
  if (!tabs.length) return null;

  return (
    <div className="mb-5 flex items-center gap-1 overflow-x-auto border-b-2 border-bzblue-400 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist">
      {tabs.map(tab =>
        tab.href ? (
          <TabLink key={tab.key} tab={tab} />
        ) : (
          <TabButton active={activeKey === tab.key} key={tab.key} onSelect={onSelect} tab={tab} />
        ),
      )}
    </div>
  );
};
